<template>
	<div class="edit-row--template scrollbar-custom">
		<div class="dashed-row mb--15">
			<div @click="templateChoose(TEMPLATE_VALUE.NONE.VALUE)" class="dashed-item flex--grow">
				<span
					:class="{'color--rosy-pink': sharedTemplateName === TEMPLATE_VALUE.NONE.VALUE}"
					class="font-weight--500"
				>{{ TEMPLATE_VALUE.NONE.NAME }}</span>
			</div>
			<div @click="templateChoose(TEMPLATE_VALUE.GLOBAL.VALUE)" class="dashed-item flex--grow">
				<span
					:class="{'color--rosy-pink': sharedTemplateName === TEMPLATE_VALUE.GLOBAL.VALUE}"
					class="font-weight--500"
				>{{ TEMPLATE_VALUE.GLOBAL.NAME }}</span>
			</div>
		</div>
		<div class="element-box element-box--edit-bar">
			<template
				v-for="(template, $indexTemplate) of objectTemplates"
				:key="`node-${$indexTemplate}`"
			>
				<div
					v-if="template.type === TEMPLATE_TYPES.NODE"
					@click="templateChoose(template.name)"
					class="element-link element-link--edit-bar"
				>
					<div
						:class="{'active': template.name === sharedTemplateName}"
						class="element-preview element-preview--edit-bar cursor--pointer">
						<header class="element-preview__header">
							<span>{{ t('objectTemplate.size') }} {{ template.width }}px</span>
							<span>{{ t('objectTemplate.font') }} {{ template.fontSize }}px</span>
						</header>
						<img
							:ref="el => snapshotsRef[$indexTemplate] = el"
							:style="{width: snapshotStyles[$indexTemplate]}"
							:src="template.snapshot" alt=""/>
					</div>
					<div class="element-link__field">
						<span>{{ template.name }}</span>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUpdate } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { TEMPLATE_TYPES } from '@/models/TEMPLATE_TYPES';
import { TEMPLATE_VALUE } from '@/models/TEMPLATE_VALUE';
import { TOASTS_TYPES } from '@/models/TOASTS_TYPES';
import ApiService from '@/utils/ApiService';
import ToastUtils from '@/utils/ToastUtils';

const { t } = useI18n();
const store = useStore();
const { createToastFromComponent } = ToastUtils();

const templates = computed(() => store.getters['design/getNodesTemplates']);
const objectTemplates = computed(() => store.getters['templates/getObjectTemplates']);
const selectedMap = computed(() => store.getters['nodes/getSelectedNodes'] as Map<number, unknown>);
const rawNodes = computed(() => store.getters['nodes/getRawNodes']);

if (!objectTemplates.value.length) {
	store.dispatch('templates/objectTemplatesInit');
}

function getTemplateId(name: string): string | null | undefined {
	if (name === TEMPLATE_VALUE.NONE.VALUE) return null;
	if (name === TEMPLATE_VALUE.GLOBAL.VALUE) return '0';
	for (const [key, value] of Object.entries(templates.value)) {
		if ((value as { name: string }).name === name) return key;
	}
}

function getTemplateName(templateId: string | null): string {
	if (templateId === '0') return TEMPLATE_VALUE.GLOBAL.VALUE;
	if (templateId && templates.value[templateId]) return templates.value[templateId].name;
	return TEMPLATE_VALUE.NONE.VALUE;
}

const sharedTemplateName = computed((): string | null => {
	const selectedIds = Array.from(selectedMap.value.keys());
	if (!selectedIds.length) return null;
	const nodes = selectedIds.map(id => selectedMap.value.get(id) as { template: string | null });
	const firstName = getTemplateName(nodes[0].template);
	const allSame = nodes.every(n => getTemplateName(n.template) === firstName);
	return allSame ? firstName : null;
});

async function templateChoose(templateName: string): Promise<void> {
	const templateId = getTemplateId(templateName);
	const selectedIds = Array.from(selectedMap.value.keys());

	// Apply template locally to all nodes first
	const nodesPayload: { id: number; design: Record<string, unknown> }[] = [];
	for (const nodeId of selectedIds) {
		const node = selectedMap.value.get(nodeId) as { id: number };
		const rawNode = rawNodes.value.find((n: { id: number }) => n.id === nodeId);
		if (!rawNode) continue;

		await store.dispatch('nodes/applyTemplate', { templateId, nodeId });
		await store.dispatch('nodes/updateConvertedRelatedEdges', [node]);
		nodesPayload.push({ id: nodeId, design: rawNode.design });
	}

	// Single batch PATCH to elements endpoint
	const mapsURL = store.getters['maps/getMapsURL'];
	const elementsURL = store.getters['maps/getElementsURL'];
	const mapId = store.getters['maps/getCurrentMap'].id;

	try {
		await ApiService.patch(`${mapsURL}${mapId}${elementsURL}`, { nodes: nodesPayload });
		await createToastFromComponent(TOASTS_TYPES.SUCCESS, t('message.templateApplySuccess'), store);
	} catch (err) {
		await createToastFromComponent(TOASTS_TYPES.ERROR, t('message.templateApplyError'), store);
	}
}

// Snapshot dimensions
const snapshotsRef = ref<HTMLImageElement[]>([]);
const snapshotStyles = ref<string[]>([]);
const pixelRatio = computed(() => window.devicePixelRatio);

onMounted(() => {
	snapshotsRef.value.forEach(snapshotRef => {
		snapshotRef.onload = () => {
			snapshotStyles.value.push(snapshotRef?.naturalWidth / pixelRatio.value + 'px');
		};
	});
});
onBeforeUpdate(() => {
	snapshotsRef.value.forEach((snapshotRef, index) => {
		if (snapshotRef) snapshotRef.onload = () => {
			snapshotStyles.value[index] = snapshotRef?.naturalWidth / pixelRatio.value + 'px';
		};
	});
});
</script>

<style lang="scss">
@use "@/scss/items/element/element-box";
@use "@/scss/items/element/element-link";
@use "@/scss/items/element/element-preview";
@use "@/scss/items/dashed/dashed-row";
@use "@/scss/items/dashed/dashed-item";
</style>
